// Borradores por conversación, solo en memoria (nunca localStorage): se pierden al recargar y se
// borran al cerrar sesión. Cada borrador lleva su clientRequestId: repetir un POST incierto con el mismo
// contenido reutiliza el ID y el servidor devuelve el mismo mensaje en vez de duplicarlo.

/** Archivo adjunto al borrador: se sube al elegirlo y se envía con su ID. */
export interface DraftAttachment {
  localId: string;
  name: string;
  size: number;
  /** Clase aproximada por el MIME del navegador; el servidor decide por el contenido. */
  kind: 'IMAGE' | 'AUDIO' | 'VIDEO' | 'DOCUMENT';
  /** Vista previa local de una imagen (URL de objeto); se libera al quitar el archivo. */
  previewUrl: string | null;
  status: 'uploading' | 'ready' | 'error';
  progress: number;
  attachmentId: string | null;
  error: string | null;
}

export interface Draft {
  text: string;
  clientRequestId: string;
  /** El último POST de este contenido quedó sin respuesta: puede haberse creado. */
  uncertain: boolean;
  attachment: DraftAttachment | null;
}

const drafts = new Map<string, Draft>();
const listeners = new Map<string, Set<() => void>>();

/** Guarda y avisa a quien mira ese borrador (una subida termina aunque el editor se haya remontado). */
function save(slot: string, draft: Draft): Draft {
  drafts.set(slot, draft);
  for (const listener of listeners.get(slot) ?? []) listener();
  return draft;
}

/** Aviso en cada cambio del borrador de `slot` (para useSyncExternalStore). Devuelve la baja. */
export function subscribeDraft(slot: string, listener: () => void): () => void {
  let set = listeners.get(slot);
  if (!set) listeners.set(slot, set = new Set());
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) listeners.delete(slot);
  };
}

function newId(): string {
  return crypto.randomUUID();
}

export function getDraft(conversationId: string): Draft {
  let draft = drafts.get(conversationId);
  if (!draft) {
    draft = { text: '', clientRequestId: newId(), uncertain: false, attachment: null };
    drafts.set(conversationId, draft);
  }
  return draft;
}

/**
 * Editar el texto crea un contenido distinto: tras un resultado incierto, el borrador nuevo lleva otro
 * ID (el servidor rechazaría el mismo ID con otro cuerpo). El hilo, que es la fuente de verdad, mostrará
 * el mensaje anterior si llegó a crearse.
 */
export function setDraftText(conversationId: string, text: string): Draft {
  const current = getDraft(conversationId);
  const next: Draft = current.uncertain && text !== current.text
    ? { ...current, text, clientRequestId: newId(), uncertain: false }
    : { ...current, text };
  return save(conversationId, next);
}

/**
 * Cambia el adjunto del borrador. Otro archivo es otro contenido: tras un envío incierto se usa un ID
 * nuevo, como al editar el texto. La vista previa anterior se libera.
 */
export function setDraftAttachment(conversationId: string, attachment: DraftAttachment | null): Draft {
  const current = getDraft(conversationId);
  if (current.attachment?.previewUrl && current.attachment.previewUrl !== attachment?.previewUrl) {
    URL.revokeObjectURL(current.attachment.previewUrl);
  }
  const changed = current.attachment?.localId !== attachment?.localId;
  const next: Draft = current.uncertain && changed
    ? { ...current, attachment, clientRequestId: newId(), uncertain: false }
    : { ...current, attachment };
  return save(conversationId, next);
}

/** Progreso o resultado de la subida del adjunto que sigue en el borrador (si no se cambió entretanto). */
export function updateDraftAttachment(conversationId: string, localId: string, changes: Partial<DraftAttachment>): Draft {
  const current = getDraft(conversationId);
  if (current.attachment?.localId !== localId) return current;
  const next: Draft = { ...current, attachment: { ...current.attachment, ...changes } };
  return save(conversationId, next);
}

export function markUncertain(conversationId: string): Draft {
  const next = { ...getDraft(conversationId), uncertain: true };
  return save(conversationId, next);
}

/** Tras un envío aceptado por la API: borrador vacío con un ID nuevo. */
export function clearDraft(conversationId: string): Draft {
  const previous = drafts.get(conversationId)?.attachment?.previewUrl;
  if (previous) URL.revokeObjectURL(previous);
  const next: Draft = { text: '', clientRequestId: newId(), uncertain: false, attachment: null };
  return save(conversationId, next);
}

/** Mismo texto con un ID nuevo: el servidor ya usó el anterior con otro contenido. */
export function renewDraftId(conversationId: string): Draft {
  const current = getDraft(conversationId);
  const next: Draft = { text: current.text, clientRequestId: newId(), uncertain: false, attachment: current.attachment };
  return save(conversationId, next);
}

/** Respuestas escritas o con archivo sin enviar (la plantilla elegida no cuenta como borrador). */
export function pendingDraftCount(): number {
  let count = 0;
  for (const [slot, draft] of drafts) if (!slot.includes('#') && (draft.text.trim().length > 0 || draft.attachment)) count += 1;
  return count;
}

export function clearAllDrafts(): void {
  for (const draft of drafts.values()) if (draft.attachment?.previewUrl) URL.revokeObjectURL(draft.attachment.previewUrl);
  drafts.clear();
  for (const set of listeners.values()) for (const listener of set) listener();
}
